import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { reportAPI, notificationAPI } from '../services/api';
import { toast } from 'react-toastify';
import Icon from '../components/Icon';
import {
  GreetingHero, FilterBar, KpiTile, SectionCard, ActivityFeed, CHART_COLORS, greetingFor
} from '../components/dashboard/DashboardKit';

export default function CommsAnalyticsDashboard() {
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const stored = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = stored.full_name || stored.firstName || stored.username || stored.email || 'there';
  const firstName = displayName.split(' ')[0];

  const load = async (r = range, cs = customStart, ce = customEnd) => {
    setLoading(true);
    try {
      const params = r === 'custom' ? (cs && ce ? { startDate: cs, endDate: ce } : { range: 'all' }) : { range: r };
      const [sRes, nRes] = await Promise.all([
        reportAPI.getDashboard(params),
        notificationAPI.getRecent(6, true)
      ]);
      setStats(sRes.data);
      setNotifications(nRes.data || []);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeRange = (r) => { setRange(r); load(r, customStart, customEnd); };
  const applyCustom = () => {
    if (!customStart || !customEnd) return toast.error('Select both dates');
    setRange('custom'); load('custom', customStart, customEnd);
  };

  const ov = stats?.overview || {};
  const performanceData = (stats?.monthlyPerformance || []).map((m) => ({
    month: m.month_name, Admissions: parseInt(m.admissions, 10) || 0, Enrollments: parseInt(m.enrollments, 10) || 0
  }));
  const regionData = (stats?.regionalData || []).map((item, i) => ({
    name: item.region, value: parseInt(item.count, 10) || 0, color: CHART_COLORS[i % CHART_COLORS.length]
  }));

  if (loading && !stats) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: 64, height: 64, fontSize: 28, margin: '0 auto 24px' }} />
          <h3 style={{ color: '#64748b', fontWeight: 600 }}>Loading Dashboard…</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content admin-analytics">
      <GreetingHero greeting={greetingFor(new Date().getHours())} name={firstName}
        actions={<><button className="btn btn-primary btn-sm" onClick={() => load()}><Icon name="refresh" /> Refresh</button></>} />

      <FilterBar range={range} onRange={changeRange} customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd} onApplyCustom={applyCustom} />

      <div className="kpi-grid">
        <KpiTile label="Active Recruiters" value={ov.activeRecruiters || 0} icon={<Icon name="users" />} color="#037b90" />
        <KpiTile label="Admissions" value={ov.totalAdmissions || 0} icon={<Icon name="user" />} color="#3b82f6" />
        <KpiTile label="Enrollments" value={ov.totalEnrollments || 0} icon={<Icon name="user-check" />} color="#10b981" />
        <KpiTile label="Pending Payments" value={ov.pendingPayments || 0} icon={<Icon name="clock" />} color="#8b5cf6" />
        <KpiTile label="Commissions (KES)" value={Number(ov.totalCommissions || 0).toLocaleString()} icon={<Icon name="dollar" />} color="#f59e0b" />
      </div>

      <div className="dash-grid">
        <SectionCard title="Admission Trends" subtitle="Admissions vs Enrollments (6 mo)">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={performanceData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cAdm" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                <linearGradient id="cEnr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="Admissions" stroke="#3b82f6" fill="url(#cAdm)" strokeWidth={2} />
              <Area type="monotone" dataKey="Enrollments" stroke="#10b981" fill="url(#cEnr)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Regional Reach" subtitle="Active recruiters by county">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={regionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {regionData.map((e, i) => (<Cell key={i} fill={e.color} />))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      <div className="dash-grid">
        <SectionCard title="Recent Activity" subtitle="Latest notifications & updates">
          <ActivityFeed items={notifications} emptyText="No recent activity." />
        </SectionCard>
        <SectionCard title="Top Recruiters" subtitle="By commission earned">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={(stats?.topRecruiters || []).slice(0, 5).map((r) => ({ name: (r.full_name || 'R').split(' ')[0], commission: parseFloat(r.total_commission || 0) }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="commission" fill="#037b90" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>
    </div>
  );
}
